/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useState, useEffect } from 'react';
import type { Plan } from '@jira/portfolio-3__plan-wizard/src/common/types/index.tsx';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { useIntl } from '@atlassian/jira-intl';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createContainer,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import type { State, Actions, ContainerProps } from './types.tsx';

const CONTROLLER_NAME = 'create-new-plan-wizard-drawer';

const initialState: State = {
	isOpen: false,
	plan: undefined,
	isPlanCreationComplete: false,
	hasPlanInvitePermission: false,
	scenarioId: undefined,
};

const Store = createStore<State, Actions>({
	name: CONTROLLER_NAME,
	initialState,
	actions: {
		openDrawer: (plan?: Plan) => {
			traceUFOPress('arj-create-plan-wizard-drawer');
			return ({ setState }) => setState({ isOpen: true, plan, isPlanCreationComplete: false });
		},
		closeDrawer:
			() =>
			({ setState }) =>
				setState({ isOpen: false, plan: undefined, scenarioId: undefined }),
		setIsPlanCreationComplete:
			(
				plan: Plan | undefined,
				hasPlanInvitePermission: boolean,
				isPlanCreationComplete = true,
				scenarioId?: number,
			) =>
			({ setState }) =>
				setState({
					isPlanCreationComplete,
					plan,
					hasPlanInvitePermission,
					scenarioId,
				}),
	},
});

export const useCreateNewPlanWizardDrawer: HookFunction<
	State,
	BoundActions<State, Actions>,
	void
> = createHook(Store);
export const CreateNewPlanWizardDrawerProvider: OverrideContainerComponent<State> = createContainer<
	State,
	Actions,
	ContainerProps
>(Store, {
	onInit:
		() =>
		({ setState }, { isOpen, plan }) => {
			setState({ isOpen, plan });
		},
});

const EXCLUDED_I18N_FILES = ['index', 'languages', 'en'];

type IntlMessages = {
	[key: string]: string;
};
type ImportMessagesForLocale = (arg1: string) => Promise<{
	default?: IntlMessages;
}>;

const FALLBACK_INTL_MESSAGES: IntlMessages = Object.freeze({});

export const importMessagesForLocale: ImportMessagesForLocale = async (rawlocale = 'en') => {
	// Filter out country code as the AFE i18n does not use them e.g en => en-US
	const locale = rawlocale.split('-')[0];
	if (!locale || EXCLUDED_I18N_FILES.includes(locale)) {
		return { default: FALLBACK_INTL_MESSAGES };
	}

	const packageName = 'jira-portfolio-3-plan-wizard';

	try {
		if (process.env.BUNDLER === 'parcel') {
			// eslint-disable-next-line jira/import/webpack-magic-comment-entries
			const i18nModule = await import(`@atlassian/${packageName}/dist/esm/i18n/*.js`);
			const result = i18nModule[locale]();
			return result;
		}

		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		const result = await import(`@atlassian/${packageName}/dist/cjs/i18n/${locale}`);
		return result;
	} catch {
		return { default: FALLBACK_INTL_MESSAGES };
	}
};

export const useImportedMessagesForLocale = (): {
	locale: string;
	loading: boolean;
	intlMessages: IntlMessages | undefined;
} => {
	const { locale } = useIntl();
	const [state, setState] = useState<{
		locale: string;
		loading: boolean;
		intlMessages: IntlMessages | undefined;
	}>({
		locale,
		loading: true,
		intlMessages: undefined,
	});

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			setState((messageState) => ({ ...messageState, loading: true }));
			const { default: intlMessages } = await importMessagesForLocale(locale);
			if (!mounted) return;
			setState((messageState) => ({ ...messageState, loading: false, intlMessages }));
		};

		load();

		return () => {
			mounted = false;
		};
	}, [locale]);

	return state;
};
